<script>
  // The shareable detail view of one Binder Update (/{short}/updates/{seq}) —
  // the full Card Change list grouped by type. Reached from a block's "+N more".
  import { route, navigate } from '../../lib/stores/router.svelte.js';
  import { updatesState, loadDetail } from '../../lib/stores/updates.svelte.js';
  import { groupChangesByType, isFoilFinish, changePrice } from '../../lib/logic/updates-logic.js';
  import { getCardImageUrl } from '../../lib/services/card-utils.js';
  import UpdateBlock from './UpdateBlock.svelte';
  import CardChangeRow from './CardChangeRow.svelte';
  import CardGrid from '../shared/CardGrid.svelte';
  import CardTile from '../shared/CardTile.svelte';

  let { binderUrl, seq } = $props();

  $effect(() => {
    if (seq) loadDetail(binderUrl, seq);
  });

  const detail = $derived(updatesState.detail);
  const grouped = $derived(detail ? groupChangesByType(detail.changes) : null);

  function backToFeed() {
    // The detail nests under its binder, so "back" goes to that binder's feed.
    const binderUrl = route.params?.binder || detail?.update?.binder_url;
    if (binderUrl) navigate('updates', { binder: binderUrl });
    else if (typeof window !== 'undefined' && window.history.length > 1) window.history.back();
    else navigate('home');
  }

  // Build the imageData object getCardImageUrl expects.
  // The demo seam provides image_uris; deployed data provides scryfall_id.
  function changeImageData(change) {
    return {
      image_uris: change.image_uris || null,
      scryfallId: change.scryfall_id || null,
    };
  }

  function changeImageUrl(change) {
    return getCardImageUrl(changeImageData(change), 'normal');
  }

  // True if this change has any image data to show
  function hasImage(change) {
    return !!(change.image_uris || change.scryfall_id);
  }

  // True if any change in a group has image data (determines grid vs row mode)
  function groupHasImages(changes) {
    return changes.some(hasImage);
  }
</script>

<!-- Per-tile footer for the image-grid layout. Branches on the change type so
     the three sections share one tile-rendering path. -->
{#snippet tileFooter(type, c, foil, price)}
  <div class="change-tile-footer change-tile-footer--{type}">
    {#if type === 'added'}
      <span class="ctf-delta ctf-delta--added">+{c.new_qty ?? 1}</span>
    {:else if type === 'quantity'}
      <span class="ctf-delta ctf-delta--quantity">{c.old_qty ?? 0}&#8594;{c.new_qty ?? 0}</span>
    {:else}
      <span class="ctf-removed-label">Removed</span>
    {/if}
    <span class="ctf-name" title={c.card_name}>
      {c.card_name || '???'}
      {#if foil}<span class="ctf-foil" title="Foil">&#9733;</span>{/if}
    </span>
    {#if type === 'added' && price > 0}
      <span class="ctf-price">${price.toFixed(2)}</span>
    {/if}
  </div>
{/snippet}

<!-- One change section (Added / Quantity changed / Removed). Renders an image
     grid when any change in the group has art, else a CardChangeRow fallback. -->
{#snippet section(type, title, changes)}
  {#if changes.length}
    <section class="change-section">
      <div class="section-header section-header--{type}">
        <span class="section-dot" aria-hidden="true"></span>
        <h2 class="section-title">{title}</h2>
        <span class="section-count">{changes.length}</span>
      </div>
      <div class="section-body">
        {#if groupHasImages(changes)}
          <CardGrid minTileWidth="140px" minTileWidthMd="130px" gap="12px" gapMd="10px">
            {#each changes as c (c.set_code + c.collector_number + c.finish)}
              <!-- removed → disabled (desaturated + dimmed, no hover lift) -->
              <CardTile
                imageUrl={changeImageUrl(c)}
                name={c.card_name || '???'}
                stackDepth={1}
                disabled={type === 'removed'}
              >
                {#snippet footer()}
                  {@render tileFooter(type, c, isFoilFinish(c.finish), changePrice(c))}
                {/snippet}
              </CardTile>
            {/each}
          </CardGrid>
        {:else}
          <!-- Fallback row layout when no images available — reuses the feed's
               row primitive instead of re-implementing it. -->
          <div class="section-rows">
            {#each changes as c (c.set_code + c.collector_number + c.finish)}
              <CardChangeRow change={c} />
            {/each}
          </div>
        {/if}
      </div>
    </section>
  {/if}
{/snippet}

<main class="update-detail">
  <!-- Breadcrumb / back navigation -->
  <nav class="breadcrumb" aria-label="Navigation">
    <button class="back-link" onclick={backToFeed}>
      <span class="back-arrow" aria-hidden="true">&#8592;</span>
      Updates
    </button>
    {#if detail}
      <span class="breadcrumb-sep" aria-hidden="true">/</span>
      <span class="breadcrumb-current">Change detail</span>
    {/if}
  </nav>

  {#if updatesState.detailLoading}
    <div class="detail-loading">
      <div class="loading-shimmer loading-shimmer--header"></div>
      <div class="loading-shimmer-grid">
        {#each Array(6) as _, i (i)}
          <div class="loading-shimmer loading-shimmer--card"></div>
        {/each}
      </div>
    </div>
  {:else if updatesState.detailError}
    <div class="detail-state-card detail-error-card">
      <span class="state-icon">&#9888;</span>
      <p class="state-message">{updatesState.detailError}</p>
    </div>
  {:else if detail}
    <!-- Binder identity block (header-only mode: no card rows inside it) -->
    <div class="detail-binder-block">
      <UpdateBlock update={detail.update} headerOnly />
    </div>

    <!-- Grouped change sections. One snippet renders all three (Added /
         Quantity / Removed); they differ only in title, dot color, the tile's
         `disabled` flag, and the per-tile footer. -->
    {#if grouped}
      <div class="change-sections">
        {@render section('added', 'Added', grouped.added)}
        {@render section('quantity', 'Quantity changed', grouped.quantity)}
        {@render section('removed', 'Removed', grouped.removed)}
      </div>
    {/if}
  {:else}
    <div class="detail-state-card">
      <span class="state-icon">&#128214;</span>
      <p class="state-message">Update not found.</p>
    </div>
  {/if}
</main>

<style>
  /* ── Page shell ── */
  .update-detail {
    max-width: 960px;
    margin: 0 auto;
    padding: var(--space-lg, 24px) var(--space-md, 16px) var(--space-xl, 32px);
    width: 100%;
    box-sizing: border-box;
  }

  /* Constrain the binder-block header to a narrower column so it doesn't
     stretch uncomfortably wide on large screens */
  .detail-binder-block {
    max-width: 660px;
  }

  /* ── Breadcrumb ── */
  .breadcrumb {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: var(--space-lg, 24px);
  }

  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--color-text-secondary, #5a5040);
    transition: color 0.12s;
  }

  .back-link:hover {
    color: var(--color-text, #2c2419);
  }

  .back-arrow {
    font-size: 1em;
    line-height: 1;
  }

  .breadcrumb-sep {
    color: var(--color-text-muted, #b5aa96);
    font-size: 0.875rem;
  }

  .breadcrumb-current {
    font-size: 0.875rem;
    color: var(--color-text-tertiary, #9a8e7c);
  }

  /* ── Loading shimmer ── */
  .detail-loading {
    display: flex;
    flex-direction: column;
    gap: var(--space-md, 16px);
  }

  .loading-shimmer {
    border-radius: var(--radius-md, 8px);
    background: linear-gradient(
      90deg,
      var(--color-bg-tray, #f3efe7) 25%,
      var(--color-border, #e6e0d5) 50%,
      var(--color-bg-tray, #f3efe7) 75%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
  }

  .loading-shimmer--header {
    height: 72px;
    max-width: 660px;
    border-radius: var(--radius-lg, 14px);
  }

  .loading-shimmer-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 12px;
  }

  .loading-shimmer--card {
    aspect-ratio: 488 / 680;
    border-radius: 0.5rem;
  }

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  /* ── State cards ── */
  .detail-state-card {
    margin-top: var(--space-xl, 32px);
    padding: var(--space-xl, 32px) var(--space-lg, 24px);
    background: var(--color-bg-surface, #fff);
    border: 1px solid var(--color-border, #e6e0d5);
    border-radius: var(--radius-lg, 14px);
    text-align: center;
    max-width: 660px;
  }

  .state-icon {
    display: block;
    font-size: 1.75rem;
    margin-bottom: var(--space-xs, 4px);
    opacity: 0.6;
  }

  .state-message {
    margin: 0;
    font-weight: 600;
    color: var(--color-text, #2c2419);
  }

  .detail-error-card {
    border-color: #fecaca;
    background: #fff5f5;
  }

  .detail-error-card .state-message {
    color: #b91c1c;
  }

  /* ── Change sections ── */
  .change-sections {
    display: flex;
    flex-direction: column;
    gap: var(--space-md, 16px);
    margin-top: var(--space-md, 16px);
  }

  .change-section {
    background: var(--color-bg-surface, #fff);
    border: 1px solid var(--color-border, #e6e0d5);
    border-radius: var(--radius-lg, 14px);
    overflow: hidden;
  }

  /* ── Section header ── */
  .section-header {
    display: flex;
    align-items: center;
    gap: var(--space-sm, 8px);
    padding: 10px var(--space-md, 16px);
    border-bottom: 1px solid var(--color-border, #e6e0d5);
    background: var(--color-bg-tray, #f3efe7);
  }

  .section-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .section-header--added .section-dot {
    background: #22c55e;
  }
  .section-header--quantity .section-dot {
    background: #f59e0b;
  }
  .section-header--removed .section-dot {
    background: #ef4444;
  }

  .section-title {
    margin: 0;
    flex: 1 1 auto;
    font-family: var(--font-sans, sans-serif);
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--color-text-secondary, #5a5040);
  }

  .section-count {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 20px;
    padding: 0 6px;
    background: var(--color-bg-surface, #fff);
    border: 1px solid var(--color-border, #e6e0d5);
    border-radius: 10px;
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--color-text-secondary, #5a5040);
  }

  /* ── Section body: holds either a CardGrid or fallback rows ── */
  .section-body {
    padding: var(--space-md, 16px);
  }

  /* ── Fallback row layout (no images) ── CardChangeRow owns each row's
     internal styling; this wrapper only stacks them. ── */
  .section-rows {
    display: flex;
    flex-direction: column;
  }

  /* ── Card grid tile footers ── */
  .change-tile-footer {
    display: flex;
    align-items: baseline;
    gap: 4px;
    flex-wrap: wrap;
    row-gap: 0;
  }

  .ctf-delta {
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
    font-size: 0.7rem;
    font-weight: 700;
    white-space: nowrap;
  }

  .ctf-delta--added {
    color: #166534;
  }
  .ctf-delta--quantity {
    color: #92400e;
  }

  .ctf-removed-label {
    flex-shrink: 0;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #991b1b;
  }

  .ctf-name {
    flex: 1 1 auto;
    font-size: 0.72rem;
    font-weight: 500;
    color: var(--color-text, #2c2419);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  .ctf-foil {
    font-size: 0.6em;
    color: #f59e0b;
    margin-left: 2px;
    vertical-align: middle;
  }

  .ctf-price {
    flex-shrink: 0;
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--color-rarity-rare, #a68c5b);
    white-space: nowrap;
  }

  /* ── Mobile ── */
  @media (max-width: 768px) {
    .update-detail {
      padding: var(--space-md, 16px) var(--space-sm, 8px) var(--space-lg, 24px);
    }

    /* Shift breadcrumb right to clear the fixed hamburger button */
    .breadcrumb {
      padding-left: 60px;
      margin-bottom: var(--space-md, 16px);
    }

    .section-body {
      padding: var(--space-sm, 8px);
    }
  }
</style>
